import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { CheckCircle2, TrendingUp, Users, Award, ArrowLeft } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  StatsFilters,
  CertificationFilter,
  AccessFilter,
  CompletionFilter,
} from '@/components/stats/StatsFilters';
import { UserDetailsModal } from '@/components/stats/UserDetailsModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ROUTES } from '@/config/routes';
import configJson from '@/config.json';
import { useAuthUser } from '@/hooks/queries';
import { useTranslations } from '@/hooks/use-translations';
import { supabase } from '@/integrations/supabase/client';
import { CERTIFICATION_CONFIG } from '@/modules/certification/constants';

interface UserStats {
  userId: string;
  username: string;
  caseSelectorVisits: number;
  caseSelectorAttempts: number;
  completions: number;
  startDate: string | null;
  nps: number | null;
  scores: string;
  status: 'completed' | 'failed';
  nbrAttempts: number;
  hasAccessed: boolean;
  hasCompleted: boolean;
}

const columnHelper = createColumnHelper<UserStats>();

const columns = [
  columnHelper.accessor('username', {
    header: 'Username',
    cell: (info) => info.getValue() || '--',
  }),
  columnHelper.accessor('caseSelectorVisits', {
    header: 'Visits',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('caseSelectorAttempts', {
    header: 'Attempts',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('completions', {
    header: 'Case completions',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('startDate', {
    header: 'Start Date',
    cell: (info) => {
      const date = info.getValue();
      return date ? new Date(date).toLocaleDateString() : '--';
    },
  }),
  columnHelper.accessor('nps', {
    header: 'NPS',
    cell: (info) => {
      const nps = info.getValue();
      return nps !== null ? `${nps}%` : '--';
    },
  }),
  columnHelper.accessor('scores', {
    header: 'Test scores',
    cell: (info) => {
      const scores = info.getValue();
      return scores ? scores : '--';
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => {
      const status = info.getValue();
      if (status === 'completed') {
        return (
          <Badge className="bg-livelinx-green text-white hover:bg-livelinx-green">Passed</Badge>
        );
      }
      if (status === 'failed') {
        return <Badge className="bg-livelinx-red text-white hover:bg-livelinx-red">Failed</Badge>;
      }
      return '--';
    },
  }),
  columnHelper.accessor('nbrAttempts', {
    header: 'Test Attempts',
    cell: (info) => {
      const attempts = info.getValue();
      return `${attempts}/${CERTIFICATION_CONFIG.MAX_ATTEMPTS}`;
    },
  }),
];

export default function Stats() {
  const { t } = useTranslations('common');
  const { caseId } = useParams<{ caseId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [certificationFilter, setCertificationFilter] = useState<CertificationFilter>('all');
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [completionFilter, setCompletionFilter] = useState<CompletionFilter>('all');
  const [selectedUser, setSelectedUser] = useState<UserStats | null>(null);
  const { data: currentUser, isLoading: userLoading } = useAuthUser();

  const { data: fullData = [], isLoading: dataLoading } = useQuery({
    queryKey: ['stats', 'full', caseId, currentUser?.id],
    queryFn: async () => {
      if (!caseId) return [];
      const isAdmin = configJson.adminEmails.includes(currentUser?.email || '');

      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username');

      if (profilesError) throw profilesError;
      if (!profiles) return [];

      const userIds = profiles.map((p) => p.id);

      // Fetch all related data in parallel
      const [statsData, loginLogsData, ratingsData, certProgressData] = await Promise.all([
        supabase.from('case_statistics').select('*').eq('case_id', caseId).in('user_id', userIds),
        supabase.from('login_logs').select('*').in('user_id', userIds),
        supabase.from('masterclass_ratings').select('*').eq('case_id', caseId),
        supabase.from('certification_progress').select('*').eq('case_id', caseId),
      ]);

      return profiles
        .map((profile): UserStats => {
          const stat = statsData.data?.find((s) => s.user_id === profile.id);
          const loginLogs = loginLogsData.data?.filter((log) => log.user_id === profile.id) || [];
          const ratings = ratingsData.data?.filter((r) => r.user_id === profile.id) || [];
          const certProgress =
            certProgressData.data
              ?.filter((cp) => cp.user_id === profile.id)
              .filter((c) => c.completed_at) || [];

          const avgRating = ratings.length
            ? (ratings.reduce((sum, r) => sum + (r.application_rating || 0), 0) /
                ratings.length /
                5) *
              100
            : null;
          const hasPassed = certProgress.some((c) => c.passed === true);
          const attemptCount = certProgress.length;

          let status: 'completed' | 'failed' = 'failed';
          if (hasPassed) status = 'completed';

          const hasAccessed = (stat?.case_selector_visits || 0) > 0 || (stat?.case_clicks || 0) > 0;
          const hasCompleted = (stat?.completions || 0) > 0;

          return {
            userId: profile.id,
            username: profile.username || profile.id,
            caseSelectorVisits: stat?.case_selector_visits || 0,
            caseSelectorAttempts: stat?.case_clicks || 0,
            completions: stat?.completions || 0,
            startDate: loginLogs[0]?.logged_in_at || null,
            nps: avgRating !== null ? Math.round(avgRating) : null,
            scores: certProgress.map((c) => `${c.score}%`).join(', '),
            status,
            nbrAttempts: attemptCount,
            hasAccessed,
            hasCompleted,
          };
        })
        .filter((el) => {
          if (isAdmin) return true;
          return el.username.startsWith('ipsen') || el.userId === currentUser?.id;
        });
    },
    enabled: !!caseId,
  });

  const loading = dataLoading || userLoading;

  const filteredData = useMemo(() => {
    let result = fullData;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((user) => user.username.toLowerCase().includes(query));
    }

    // Certification filter
    if (certificationFilter !== 'all') {
      result = result.filter((user) => user.status === certificationFilter);
    }

    // Access filter
    if (accessFilter === 'accessed') {
      result = result.filter((user) => user.hasAccessed);
    } else if (accessFilter === 'not-accessed') {
      result = result.filter((user) => !user.hasAccessed);
    }

    // Completion filter
    if (completionFilter === 'completed') {
      result = result.filter((user) => user.hasCompleted);
    } else if (completionFilter === 'not-completed') {
      result = result.filter((user) => !user.hasCompleted);
    }

    return result;
  }, [fullData, searchQuery, certificationFilter, accessFilter, completionFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate(ROUTES.AUTH);
      }
    };
    void checkAuth();
  }, [navigate]);

  const exportToCSV = () => {
    const headers = [
      t('stats.username'),
      t('stats.visits'),
      t('stats.attempts'),
      t('stats.completions'),
      t('stats.startDate'),
      t('stats.nps'),
      t('stats.testScores'),
      t('stats.status'),
      t('stats.testAttempts'),
      t('stats.hasAccessed'),
      t('stats.hasCompleted'),
    ];

    const csvData = filteredData.map((user) => [
      user.username || '--',
      user.caseSelectorVisits,
      user.caseSelectorAttempts,
      user.completions,
      user.startDate ? new Date(user.startDate).toLocaleDateString() : '--',
      user.nps !== null ? `${user.nps}%` : '--',
      user.scores || '--',
      user.status === 'completed' ? t('stats.passed') : t('stats.failed'),
      `${user.nbrAttempts}/${CERTIFICATION_CONFIG.MAX_ATTEMPTS}`,
      user.hasAccessed ? t('stats.yes') : t('stats.no'),
      user.hasCompleted ? t('stats.yes') : t('stats.no'),
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `statistics-${caseId}-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCertificationFilter('all');
    setAccessFilter('all');
    setCompletionFilter('all');
  };

  const stats = useMemo(() => {
    const total = fullData.length;
    const passed = fullData.filter((u) => u.status === 'completed').length;
    const accessed = fullData.filter((u) => u.hasAccessed).length;
    const completed = fullData.filter((u) => u.hasCompleted).length;

    return { total, passed, accessed, completed };
  }, [fullData]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-lg">{t('stats.loading')}</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto">
      <div className="container mx-auto px-4 py-4 md:py-10">
        <Button
          variant="outline"
          className="mb-4 border-livelinx-lightgray100 bg-white text-livelinx-purple200 transition-all duration-200 hover:border-livelinx-purple100 hover:bg-livelinx-purple50 hover:text-livelinx-purple200"
          onClick={() => navigate(ROUTES.CASES)}
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          {t('buttons.back')}
        </Button>

        <div className="mb-6">
          <h1 className="mb-2 font-heading text-2xl text-livelinx-purple200 md:text-4xl">
            {t('stats.title')}
          </h1>
          <p className="text-livelinx-grey200">
            {t('stats.case')}: {caseId}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.total')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.total}</p>
                </div>
                <Users className="h-8 w-8 text-livelinx-blue300" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.passed')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.passed}</p>
                </div>
                <Award className="h-8 w-8 text-livelinx-green" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.accessed')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.accessed}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-livelinx-blue300" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.completed')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.completed}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-livelinx-purple200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <StatsFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            certificationFilter={certificationFilter}
            onCertificationFilterChange={setCertificationFilter}
            accessFilter={accessFilter}
            onAccessFilterChange={setAccessFilter}
            completionFilter={completionFilter}
            onCompletionFilterChange={setCompletionFilter}
            onExport={exportToCSV}
            totalCount={fullData.length}
            filteredCount={filteredData.length}
            onClearFilters={clearFilters}
          />
        </div>

        {/* Mobile Card View */}
        <div className="space-y-3 pb-20 md:hidden">
          {filteredData.length > 0 ? (
            filteredData.map((user, idx) => (
              <Card
                key={idx}
                className="cursor-pointer shadow-white transition-shadow hover:shadow-blue-shadow"
                onClick={() => setSelectedUser(user)}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between gap-2 font-body text-base">
                    <span className="flex-1 truncate">{user.username || '--'}</span>
                    <div className="flex shrink-0 gap-1">
                      {user.status === 'completed' && (
                        <Badge className="bg-livelinx-green text-xs text-white hover:bg-livelinx-green">
                          {t('stats.passed')}
                        </Badge>
                      )}
                      {user.status === 'failed' && (
                        <Badge className="bg-livelinx-red text-xs text-white hover:bg-livelinx-red">
                          {t('stats.failed')}
                        </Badge>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-livelinx-grey200">{t('stats.visits')}:</span>
                      <span className="ml-1 font-body">{user.caseSelectorVisits}</span>
                    </div>
                    <div>
                      <span className="text-livelinx-grey200">{t('stats.completions')}:</span>
                      <span className="ml-1 font-body">{user.completions}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {user.hasAccessed && (
                      <Badge variant="outline" className="bg-livelinx-blue50 text-xs">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('stats.accessed')}
                      </Badge>
                    )}
                    {user.hasCompleted && (
                      <Badge variant="outline" className="bg-livelinx-green/10 text-xs">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('stats.completed')}
                      </Badge>
                    )}
                  </div>
                  <div className="pt-2 text-xs text-livelinx-grey200">
                    {t('stats.clickForDetails')}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-livelinx-grey200">
                {t('stats.noResults')}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden overflow-x-auto rounded-md border md:block">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="text-center font-heading">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer transition-colors hover:bg-livelinx-purple50"
                    onClick={() => setSelectedUser(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="text-center">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-livelinx-grey200"
                  >
                    {t('stats.noResults')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* User Details Modal */}
        <UserDetailsModal
          user={selectedUser}
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      </div>
    </div>
  );
}
